/**
 * Travel guide defaults from docs PDFs (Fly / Bus / Self Drive).
 * Stored as JSON in website_settings.travel_guide_json; admin can override.
 */

export const TRAVEL_GUIDE_SETTING_KEY = 'travel_guide_json';

const COMMON_REQUIREMENTS = {
  id: 'travel-requirements',
  title: 'Travel requirements',
  body: `Please carry:

• Original Passport OR
• Original Kenyan National ID (Kenyan Citizens)
• Yellow Fever Certificate (Compulsory)
• Visa (where applicable for non-East African citizens)`,
};

const COMMON_ISP = {
  id: 'interstate-pass',
  title: 'Travelling with a Kenyan National ID',
  body: `Although Kenyan citizens can travel to Uganda using a valid Kenyan National ID, you may also apply for an Inter-State Pass (ISP) before travelling. The Inter-State Pass is a free travel document issued by the Directorate of Immigration Services to facilitate travel between Kenya and Uganda.

Steps to apply online:

1. Log in to your eCitizen account via eCitizen Portal. If you do not have an account, create one first.
2. From the dashboard, select Directorate of Immigration Services under New Applications.
3. Scroll down and select Inter-State Pass Application.
4. Complete the online application form with your personal details.
5. Upload the required supporting documents:
   • A copy of your Kenyan National ID (or Foreigner Certificate/Alien Card for eligible residents).
   • A copy of your Birth Certificate (for minors, include the child's birth certificate and parental consent where applicable).
6. Submit the application. There is no application fee (the service is free).
7. Once your application has been processed, log back into your eCitizen account to download and print your Inter-State Pass.
8. When travelling, present the printed Inter-State Pass together with your original Kenyan National ID (or the original Birth Certificate for minors) at the border.`,
};

const COMMON_YELLOW_FEVER = {
  id: 'yellow-fever',
  title: 'Yellow Fever Certificate (Compulsory)',
  body: `A valid Yellow Fever Vaccination Certificate is compulsory when travelling between Kenya and Uganda.

Travelers may be requested to present their certificate by immigration or health officials.

Please ensure you carry the original certificate with your travel documents.`,
};

const COMMON_MONEY = {
  id: 'money',
  title: 'Money',
  body: `Recommended currencies:

• Kenyan Shillings (KES)
• Ugandan Shillings (UGX)

Visa and Mastercard are widely accepted in major hotels, restaurants and supermarkets.`,
};

export const TRAVEL_GUIDE_DEFAULTS = {
  page_title: 'Travel guide',
  page_subtitle: 'Choose how you will travel to Uganda for the celebration',
  closing_note:
    'We wish you a safe journey and look forward to celebrating this special occasion with you!',
  guides: [
    {
      id: 'fly',
      title: 'Fly to Uganda',
      short_description: 'Quickest option from Kenya or abroad',
      intro:
        'Flying is the quickest and most convenient option for guests travelling from Kenya or abroad.',
      sections: [
        {
          id: 'arrival-airport',
          title: 'Arrival Airport',
          body: 'Entebbe International Airport (EBB)',
        },
        {
          id: 'flight-duration',
          title: 'Flight Duration',
          body: `Nairobi (JKIA) → Entebbe (EBB)

Approximately 1 hour 15 minutes – 1 hour 30 minutes`,
        },
        {
          id: 'airlines',
          title: 'Recommended Airlines',
          body: `Please book directly through the official websites below.

• Kenya Airways — https://www.kenya-airways.com/en-ke/
• Uganda Airlines — https://ugandairlines.com/
• Safarilink Aviation — https://www.flysafarilink.com`,
        },
        {
          id: 'recommended-arrival',
          title: 'Recommended Arrival',
          body: 'We recommend arriving in Uganda at least one day before the ceremony to allow enough time to settle in before the celebrations.',
        },
        {
          id: 'airport-to-venue',
          title: 'Airport to the Ceremony Venue',
          body: `Venue: Buziga Hill View, Kampala

Approximate distance from Entebbe International Airport: 45 km

Estimated travel time: 1 hour – 1 hour 30 minutes, depending on traffic.`,
        },
        { ...COMMON_REQUIREMENTS },
        { ...COMMON_ISP },
        { ...COMMON_YELLOW_FEVER },
        {
          id: 'mobile-network',
          title: 'Mobile Network',
          body: `We highly recommend purchasing an MTN Uganda SIM Card, as MTN has the widest network coverage throughout Uganda.

Airtel Uganda is also a reliable alternative.

Where to Purchase — the easiest and safest places to obtain a SIM card are:

• MTN Shop at Entebbe International Airport Arrivals Hall
• Airtel Shop at Entebbe International Airport

Please carry your passport (only one accepted for non-Ugandan citizens), as SIM registration is mandatory in Uganda.`,
        },
        { ...COMMON_MONEY },
        {
          id: 'tips',
          title: 'Helpful Travel Tips',
          body: `• Book flights as early as possible.
• Keep your travel documents together and easily accessible.
• Save offline Google Maps before travelling.
• Avoid exchanging money with unofficial currency dealers.
• Keep both printed and digital copies of your important travel documents.
• Share your travel itinerary with a family member or friend.`,
        },
      ],
    },
    {
      id: 'bus',
      title: 'Bus Travel to Uganda',
      short_description: 'Comfortable overnight coach from Nairobi',
      intro: 'Travelling by bus is a popular option for guests coming from Kenya.',
      sections: [
        {
          id: 'journey-time',
          title: 'Estimated Journey Time',
          body: `Nairobi → Kampala

Approximately 12–14 hours

(The duration depends on traffic and border crossing times.)`,
        },
        {
          id: 'bus-companies',
          title: 'Recommended Bus Companies',
          body: `Please book directly through the official websites below.

• Mash Poa — https://mashpoaexpress.com
• Buscar — https://bus-car.co.ke`,
        },
        {
          id: 'other-buses',
          title: 'Other Reliable Bus Companies',
          body: `The following companies are also reliable but currently do not have official online booking systems.

To avoid fraud or scams, please purchase tickets directly from their official booking offices or designated bus terminals.

• Mozaico Coaches Limited
• Bebeto Coach Services
• Trinity Express Bus
• Eco Bus Limited
• Simba Coach`,
        },
        {
          id: 'bus-arrival',
          title: 'Bus Arrival in Kampala',
          body: `Most buses terminate within Kampala City Centre.

Approximate distance from Kampala City Centre to Buziga Hill View: 10–12 km

Estimated travel time: 25–40 minutes depending on traffic.

Transport options from Kampala Bus Park include:

• Uber
• SafeBoda`,
        },
        { ...COMMON_REQUIREMENTS },
        { ...COMMON_ISP },
        { ...COMMON_YELLOW_FEVER },
        {
          id: 'mobile-network',
          title: 'Mobile Network',
          body: `We highly recommend purchasing an MTN Uganda SIM Card, as MTN has the widest network coverage throughout Uganda.

Airtel Uganda is also a reliable alternative.

Where to Purchase — the easiest and safest places to obtain a SIM card are:

• Any official MTN or Airtel Service Centre

Please carry your passport (only one accepted for non-Ugandan citizens), as SIM registration is mandatory in Uganda.`,
        },
        { ...COMMON_MONEY },
        {
          id: 'tips',
          title: 'Helpful Travel Tips',
          body: `• Book bus tickets as early as possible.
• Keep your travel documents together and easily accessible.
• Save offline Google Maps before travelling.
• Avoid exchanging money with unofficial currency dealers.
• Arrive in Kampala at least one day before the ceremony.
• Keep both printed and digital copies of your important travel documents.
• Share your travel itinerary with a family member or friend.`,
        },
      ],
    },
    {
      id: 'drive',
      title: 'Self Drive to Uganda',
      short_description: 'Drive your own vehicle via Busia or Malaba',
      intro:
        'Driving gives you flexibility and allows you to explore Uganda at your own pace.',
      sections: [
        {
          id: 'journey-time',
          title: 'Estimated Journey Time',
          body: `Nairobi → Kampala

Approximately 10–13 hours

This includes:

• Fuel stops
• Meal breaks
• Border crossing
• Immigration procedures`,
        },
        {
          id: 'border',
          title: 'Border Crossing Information',
          body: `Recommended Border Crossing:

• Busia Border Post
• Malaba Border Post

Whichever border you choose, please allow 30 minutes to 1 hour for immigration and customs procedures depending on traffic.`,
        },
        { ...COMMON_REQUIREMENTS },
        { ...COMMON_ISP },
        { ...COMMON_YELLOW_FEVER },
        {
          id: 'vehicle-docs',
          title: 'Required Vehicle Documents',
          body: `Drivers should carry:

• Original Vehicle Logbook
• Valid Driving License
• Valid Motor Vehicle Insurance
• COMESA Yellow Card Insurance
• Vehicle Owner's Authorization Letter (if driving a vehicle registered in another person's name)`,
        },
        {
          id: 'mobile-network',
          title: 'Mobile Network',
          body: `We highly recommend purchasing an MTN Uganda SIM Card, as MTN has the widest network coverage throughout Uganda.

Airtel Uganda is also a reliable alternative.

Where to Purchase — the easiest and safest places to obtain a SIM card are:

• Any official MTN or Airtel Service Centre within Kampala

Please carry your passport (only one accepted for non-Ugandan citizens), as SIM registration is mandatory in Uganda.`,
        },
        { ...COMMON_MONEY },
        {
          id: 'tips',
          title: 'Helpful Travel Tips',
          body: `• Keep your travel documents together and easily accessible.
• Save offline Google Maps before travelling.
• Avoid exchanging money with unofficial currency dealers.
• If driving, begin your journey early to avoid driving late at night.
• Arrive in Kampala at least one day before the ceremony.
• Keep both printed and digital copies of your important travel documents.
• Share your travel itinerary with a family member or friend.`,
        },
      ],
    },
  ],
};

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

/** Parse settings.travel_guide_json (or return defaults). */
export function travelGuideFromSettings(settings) {
  const raw = settings?.[TRAVEL_GUIDE_SETTING_KEY];
  if (!raw || String(raw).trim() === '') {
    return deepClone(TRAVEL_GUIDE_DEFAULTS);
  }
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || !Array.isArray(parsed.guides) || parsed.guides.length === 0) {
      return deepClone(TRAVEL_GUIDE_DEFAULTS);
    }
    return {
      page_title: String(parsed.page_title || TRAVEL_GUIDE_DEFAULTS.page_title).trim() || TRAVEL_GUIDE_DEFAULTS.page_title,
      page_subtitle:
        String(parsed.page_subtitle || TRAVEL_GUIDE_DEFAULTS.page_subtitle).trim() ||
        TRAVEL_GUIDE_DEFAULTS.page_subtitle,
      closing_note:
        String(parsed.closing_note ?? TRAVEL_GUIDE_DEFAULTS.closing_note).trim() ||
        TRAVEL_GUIDE_DEFAULTS.closing_note,
      guides: parsed.guides.map((g, i) => ({
        id: String(g.id || `guide-${i}`),
        title: String(g.title || `Option ${i + 1}`).trim(),
        short_description: String(g.short_description || '').trim(),
        intro: String(g.intro || '').trim(),
        sections: Array.isArray(g.sections)
          ? g.sections.map((s, j) => ({
              id: String(s.id || `section-${j}`),
              title: String(s.title || `Section ${j + 1}`).trim(),
              body: String(s.body || '').trim(),
            }))
          : [],
      })),
    };
  } catch {
    return deepClone(TRAVEL_GUIDE_DEFAULTS);
  }
}

export function serializeTravelGuide(guide) {
  return JSON.stringify(guide ?? TRAVEL_GUIDE_DEFAULTS);
}

/** Render body text: paragraphs + auto-link http(s) URLs. */
export function renderTravelBodyLines(body) {
  return String(body || '')
    .split(/\n/)
    .map((line) => line.trimEnd());
}
